"use client";

import { useState } from "react";

// eslint-disable-next-line import/named
import { FieldValues, useForm } from "react-hook-form";
import { useImmer } from "use-immer";
import { createFormFields } from "./model";
import { preSubmit } from "@/forms/validation";

import { clientPost } from "@/service/client-api/client-api";
import { vestResolver } from "@hookform/resolvers/vest";
import { fromFormDefinitionToValidationSchema } from "@/forms/validation/validation-factory";
import { Dialog, DialogClose, DialogContent } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { CheckIcon, LoaderIcon } from "lucide-react";
import { TextField } from "@/forms/fields/text-field/text-field";
import { SelectField } from "@/forms/fields/select-field/select-field";
import { Button } from "@/components/ui/button";
import { FormError } from "@/forms/form-error";
import { handleUserError } from "@/service/client-api/error-handler";
import { FieldsContainer } from "@/forms/fields-container";

export function SendMedicalFacility({
  filterSource,
}: {
  filterSource: ResidencyMapFilterSource;
}) {
  const [fields, setFields] = useImmer(createFormFields(filterSource));
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const form = useForm({
    resolver: vestResolver(fromFormDefinitionToValidationSchema(fields)),
  });
  const { reset } = form;

  const onSubmit = async ({
    facilityName,
    unitName,
    specialization,
    city,
  }: FieldValues) => {
    if (preSubmit(loading, setLoading, setError, setFields) === "BREAK") {
      return;
    }

    try {
      await clientPost(
        "residency.missing_facility_report",
        {},
        {
          city,
          facilityName,
          unitName,
          specialization: specialization.value,
        },
      );

      setSuccess(true);
      setLoading(false);

      setFields(() => createFormFields(filterSource));

      reset({ message: "" });
    } catch (caughtError) {
      setLoading(false);
      handleUserError(caughtError, setError, setFields, setSuccess);
    }
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)} variant={"blue"}>
        Zgłoś brak placówki
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className={"w-[336px]"} innerClassName={"px-6 py-10"}>
          {!success ? (
            <>
              <h3 className={"text-md mb-4 font-semibold"}>
                Zgłoś brak placówki
              </h3>

              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <FieldsContainer>
                    <TextField field={fields.city} />
                    <TextField field={fields.facilityName} />
                    <TextField field={fields.unitName} />
                    <SelectField
                      field={fields.specialization}
                      renderWithoutPortal={true}
                    />
                  </FieldsContainer>

                  <Button
                    variant={"blue"}
                    type={"submit"}
                    className={"flex"}
                    disabled={loading}
                  >
                    {loading && <LoaderIcon className="animate-spin" />}
                    Zgłoś
                  </Button>

                  <FormError error={error} />
                </form>
              </Form>
            </>
          ) : (
            <div className={"mx-auto w-[16rem] text-center"}>
              <div className={"mb-4 flex justify-center"}>
                <CheckIcon size={"24px"} />
              </div>
              <h3 className={"mb-2 font-semibold"}>
                Dziękujemy za zgłoszenie!
              </h3>
              <p className={"text-sm"}>
                Postaramy się jak najszybciej dodać podaną placówkę.
              </p>
            </div>
          )}
          <DialogClose />
        </DialogContent>
      </Dialog>
    </>
  );
}
